import { useMemo } from "react";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { useHeadings } from "@/store/editorSelectors";
import styles from "../blocks/blocks.module.scss";

interface NumberedHeading {
  id: string;
  text: string;
  level: number;
  number: string;
}

// Detects if text starts with a section number like "1.1." or "2.3.1"
const LEADING_NUMBER_PATTERN = /^(\d+(?:\.\d+)*)[.\s]/;

export function generateNumberedHeadings(headings: { id: string; text: string; level: number }[]): NumberedHeading[] {
  // Check if headings already contain embedded numbering (e.g., from DOCX import).
  // If most headings have numbers in their text, skip auto-numbering.
  const numberedCount = headings.filter(h => LEADING_NUMBER_PATTERN.test(h.text.trim())).length;
  const hasExistingNumbering = headings.length > 0 && numberedCount > headings.length * 0.5;

  if (hasExistingNumbering) {
    return headings.map(heading => {
      const match = heading.text.trim().match(LEADING_NUMBER_PATTERN);
      return {
        ...heading,
        number: match ? match[1] : "",
        text: match ? heading.text.trim().replace(LEADING_NUMBER_PATTERN, "").trim() : heading.text,
      };
    });
  }

  const counters = [0, 0, 0, 0, 0, 0];
  const result: NumberedHeading[] = [];

  for (const heading of headings) {
    const level = Math.min(heading.level, 6);
    const levelIndex = level - 1;
    counters[levelIndex]++;
    for (let i = levelIndex + 1; i < counters.length; i++) counters[i] = 0;

    // Every level above counts, zero included, as LaTeX prints it: a subsection
    // before any section is 0.1, not "1" beside the section that is 1.
    const numberParts: number[] = [];
    for (let i = 0; i <= levelIndex; i++) numberParts.push(counters[i]);
    result.push({ ...heading, number: numberParts.join(".") });
  }
  return result;
}

export function TocNodeView({ node, selected }: NodeViewProps) {
  const title = (node.attrs.title as string) || "Table of Contents";
  const headings = useHeadings();
  const numberedHeadings = useMemo(() => generateNumberedHeadings(headings), [headings]);

  const handleClick = (headingId: string) => {
    const el = document.querySelector(`[data-block-id="${headingId}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  return (
    <NodeViewWrapper data-type="toc-block" data-block-id={node.attrs.blockId} className={selected ? "ProseMirror-selectednode" : ""}>
      <div className={styles.tocBlock}>
        <h2 className={styles.tocTitle}>{title}</h2>
        {numberedHeadings.length > 0 ? (
          <nav className={styles.tocNav}>
            <ul className={styles.tocList}>
              {numberedHeadings.map((heading) => (
                <li key={heading.id} className={`${styles.tocItem} ${styles[`tocLevel${heading.level}`]}`}>
                  <button className={styles.tocLink} onClick={() => handleClick(heading.id)} title="Jump to section">
                    <span className={styles.tocNumber}>{heading.number}</span>
                    <span className={styles.tocText}>{heading.text}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        ) : (
          <p className={styles.tocEmpty}>No headings in this document.</p>
        )}
      </div>
    </NodeViewWrapper>
  );
}
